<script setup>
import { reactive } from "vue";
import AppIcon from "./AppIcon.vue";
import ShelfHeader from "./ShelfHeader.vue";

import { addStory } from "@/assets/js/storylets";
import { sanitiseData } from "@/utils/storyData.js";

let appStore = reactive({
  projectsWindow: true,
  projectsData: {},
  storyletCount: 0,
  wordCount: 0,
});

const props = defineProps({
  edit: {
    type: Object,
    required: true,
  },
  session: {
    type: Object,
    required: true,
  },
  apiUrl: {
    type: String,
    required: true,
  },
  backend: {
    type: Object,
    required: true,
  },
  data: {
    type: Object,
    required: true,
  },
  uploadedData: {
    type: Object,
    required: true,
  },
  importActions: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  "set-story",
  "close-win",
  "download-data",
  "upload-data",
  "cancel-import",
  "save-data",
]);

const fetchProjects = async () => {
  console.log("Getting projects for ", props.session.user);
  cancelAdd();
  const data = await props.backend.projects(props.session.user);
  appStore.projectsData = data;
  appStore.projectsWindow = true;
  countStorylets();
  countWords();
};

const fetchStoryData = async (project) => {
  const data = await props.backend.load(props.session.user, project.id);
  const sanitisedData = sanitiseData(data);

  props.edit.currentProjectId = project.id;
  props.edit.currentProjectName = project.name;
  emit("set-story", sanitisedData);
  appStore.projectsWindow = false;
};

function deleteStoryDataConfirm(project) {
  localState.projectDelete = project.id;
}

function deleteStoryDataCancel() {
  localState.projectDelete = "";
}

const deleteStoryData = async (project) => {
  await props.backend.delete(props.session.user, project.id);
  fetchProjects();
  localState.projectDelete = "";
};

function countStorylets() {
  if (!props.data.stories) return;
  appStore.storyletCount = Object.keys(props.data.stories).length;
}

function countWords() {
  if (!props.data.stories) return;
  let words = 0;
  /*
  let defaultLanguage = Object.keys(props.data.loc["na"])[0];

  Object.keys(props.data.loc).forEach((key) => {
    if (props.data.loc[key][defaultLanguage]) {
      words += props.data.loc[key][defaultLanguage].split(/\s+/).length;
    }
  });
*/
  appStore.wordCount = words;
}

function saveData() {
  emit("save-data");
}

function downloadData() {
  emit("download-data");
}

function uploadData() {
  emit("upload-data");
}

function cancelImport() {
  localState.importMode = null;
  emit("cancel-import");
}

function replaceStory() {
  emit("set-story", JSON.parse(JSON.stringify(props.uploadedData)));
  cancelImport();
}

let localState = reactive({
  projAdd: false,
  projNameTemp: "",
  projDescriptionTemp: "",
  projectDelete: "",
  importMode: null,
});

function closeWin() {
  emit("close-win");
  cancelImport();
  appStore.projectsWindow = false;
}

function addProject() {
  localState.projAdd = true;
}

function cancelAdd() {
  localState.projNameTemp = "";
  localState.projDescriptionTemp = "";
  localState.projAdd = false;
}

function addNow() {
  props.backend
    .addproject(props.session.user, localState.projNameTemp, localState.projDescriptionTemp)
    .then(() => fetchProjects());
}

defineExpose({ fetchProjects });

function formatDate(isodate) {
  let formattedDate = new Date(isodate).toLocaleString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return formattedDate;
}


const lookup = {
  nothing: "Do nothing",
  replacewith: "Replace with",
};

function addStorylet() {
  props.importActions.push({
    destinationStoryKey: "new",
    storyNewName: "",
    action: "replacewith",
    uploadedStoryKey: "",
  });
}

function extractStoryContent(thisData, storyKey) {
  const result = {
    linkTexts: [],
    paragraphTexts: [],
  };

  // Check if the story exists
  if (!thisData.stories || !thisData.stories[storyKey]) {
    console.error(`Story '${storyKey}' not found`);
    return result;
  }

  const story = thisData.stories[storyKey];

  // Iterate through all cards in the story
  for (const cardId in story.cards) {
    const card = story.cards[cardId];

    // Extract linkText from links
    if (card.links && Array.isArray(card.links)) {
      card.links.forEach((link) => {
        if (link.linkText) {
          result.linkTexts.push(link.linkText);
        }
      });
    }

    // Extract text from paragraphs
    if (card.paragraphs && Array.isArray(card.paragraphs)) {
      card.paragraphs.forEach((paragraph) => {
        if (paragraph.text) {
          result.paragraphTexts.push(paragraph.text);
        }
      });
    }
  }

  // Get the actual text values from the loc object
  //result.linkTexts = result.linkTexts.map((id) => {
  //  return data.loc[id]?.[data.currentLanguage] || id;
  //});

  //result.paragraphTexts = result.paragraphTexts.map((id) => {
  //  return data.loc[id]?.[data.currentLanguage] || id;
  //});

  return result;
}

// Example usage:
// const extracted = extractStoryContent(yourJsonData, "main");
// console.log(extracted);

function continueImport(name) {
  for (let i = 0; i < props.importActions.length; i++) {
    let thisAction = props.importActions[i];

    console.log(thisAction);

    if (thisAction.action == "replacewith") {
      let newKey = "";
      if (thisAction.destinationStoryKey == "new") {
        newKey = addStory(thisAction.storyNewName, props.data);
      } else {
        newKey = thisAction.destinationStoryKey;
      }

      props.data.stories[newKey].cards = JSON.parse(
        JSON.stringify(
          props.uploadedData.stories[thisAction.uploadedStoryKey].cards
        )
      );

      const extracted = extractStoryContent(
        props.uploadedData,
        thisAction.uploadedStoryKey
      );

      for (const paragraph in extracted.paragraphTexts) {
        let lockey = extracted.paragraphTexts[paragraph];
        props.data.loc[lockey] = props.uploadedData.loc[lockey];
      }

      for (const link in extracted.linkTexts) {
        let lockey = extracted.linkTexts[link];
        props.data.loc[lockey] = props.uploadedData.loc[lockey];
      }
    }
  }
  cancelImport();
}
</script>

<template>
  <div class="basic-window" v-if="appStore.projectsWindow">
    <div class="ui-container">
      <ShelfHeader title="Projects" @close-win="closeWin()">
        <template #actions>
          <button v-if="!localState.projAdd" @click="addProject()" title="New project"
            class="ui-button ui-button-green ui-button-medium ui-cornerbutton-almost">
            <AppIcon name="plus" />
          </button>
        </template>
      </ShelfHeader>
      <!-- Step 1: choose import mode -->
      <div v-if="props.uploadedData.stories && !localState.importMode" class="project-info ui-controls">
        <h4>File uploaded</h4>
        <div class="ui-sub-container">How would you like to import this file?</div>
        <div class="bg-vars">
          <div class="ui-x">
            <button class="ui-button ui-button-green ui-button-medium" @click="replaceStory()">
              Replace whole story
            </button>
          </div>
          <div class="ui-x">
            <button class="ui-button ui-button-blue ui-button-medium" @click="localState.importMode = 'pick'">
              Pick storylets
            </button>
          </div>
          <div class="ui-x">
            <button class="ui-button ui-button-medium" @click="cancelImport()">
              Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: pick storylets (existing non-functional UI) -->
      <div v-if="props.uploadedData.stories && localState.importMode === 'pick'" class="project-info ui-controls">
        <h4>Pick storylets</h4>
        <div class="ui-sub-container">
          Left column shows your current storylets. Here you can add new ones,
          and load storylets from the imported project to replace existing
          storylets or into your newly created ones.
        </div>
        <table class="import-table">
          <thead>
            <tr>
              <th>Current</th>
              <th>Action</th>
              <th>New</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in props.importActions">
              <td v-if="props.data.stories[item.destinationStoryKey]">
                {{
                  props.data.loc[
                  props.data.stories[item.destinationStoryKey].info.name
                  ]["en"]
                }}
              </td>
              <td v-if="!props.data.stories[item.destinationStoryKey]">
                <input v-model="item.storyNewName" />
              </td>
              <td>
                <select v-model="item.action" class="ui-select">
                  <option v-bind:key="cindex" v-for="(cao, cindex) in ['nothing', 'replacewith']" :value="cao">
                    {{ lookup[cao] }}
                  </option>
                </select>
              </td>
              <td v-if="item.action !== 'nothing'">
                <select v-model="item.uploadedStoryKey" class="ui-select">
                  <option v-for="(story, storyIndex) in props.uploadedData.stories" v-bind:key="storyIndex"
                    :value="storyIndex">
                    {{ props.uploadedData.loc[story.info.name]["en"] }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <button title="Add storylet" class="ui-button ui-button-blue ui-button-medium button-repad"
                  @click="addStorylet()">
                  Add storylet
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
        <div class="panel-buttons">
          <div class="expanded-button">
            <button title="Continue import" class="ui-button ui-button-green ui-button-medium button-repad"
              @click="continueImport()">
              Continue with merge/import
            </button>
            <button class="ui-button ui-button-medium button-repad" @click="cancelImport()">
              Cancel
            </button>
          </div>
        </div>
      </div>
      <div v-if="props.data.stories && !props.uploadedData.stories" class="project-info ui-controls">
        <h4>Current: {{ props.edit.currentProjectName }}</h4>
        <div class="bg-vars">
          <div class="row">
            <div class="grow">
              <span class="label">Word count:</span>&nbsp;
              <span>{{ appStore.wordCount }}</span>
            </div>
          </div>
          <div class="row">
            <div class="grow">
              <span class="label">Storylets:</span>&nbsp;<span>{{
                appStore.storyletCount
                }}</span>
            </div>
          </div>
          <div class="row">
            <div class="grow">
              <span class="label">Languages:</span>&nbsp;<span v-for="(language, index) in props.data.loc['na']">{{
                language }}&nbsp;</span>
            </div>
          </div>
          <div class="row">
            <div class="grow">
              <span class="label">Last save:</span>&nbsp;<span>
                {{ formatDate(props.data.extra.lastSave) }}
              </span>
            </div>
          </div>
          <div v-if="props.data.stories" class="panel-buttons">
            <div class="expanded-button">
              <button title="Save data" class="ui-button ui-button-blue ui-button-medium button-repad"
                @click="saveData()">
                Save data
              </button>
              <button title="Download data" class="ui-button ui-button-blue ui-button-medium button-repad"
                @click="downloadData()">
                Download data
              </button>

              <button title="Upload" class="ui-button ui-button-blue ui-button-medium button-repad"
                @click="uploadData()">
                Upload from disk
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="ui-sub-container ui-controls" v-if="!localState.projAdd && !props.uploadedData.stories">
        <h4 class="projects-list">Your projects</h4>
        <div class="list-item" v-bind:key="index" v-for="(project, index) in appStore.projectsData.projects">
          <div class="floatbuttons">
            <div class="button-container">
              <button v-if="localState.projectDelete == ''" title="Open project"
                class="ui-button ui-button-blue ui-button-medium button-repad" @click="fetchStoryData(project)">
                <AppIcon name="folder-open" />
              </button>
              <button v-if="localState.projectDelete == ''" title="Delete project"
                @click="deleteStoryDataConfirm(project)" class="ui-button ui-button-red ui-button-medium button-repad">
                <AppIcon name="trash-can" />
              </button>

              <button v-if="localState.projectDelete == project.id" title="Confirm Delete project"
                @click="deleteStoryData(project, index)" class="ui-button ui-button-red ui-button-medium button-repad">
                <AppIcon name="trash-can" />&nbsp;Delete?
              </button><button v-if="localState.projectDelete == project.id" title="Cancel Delete project"
                @click="deleteStoryDataCancel()" class="ui-button ui-button-blue ui-button-medium button-repad">
                <AppIcon name="trash-can" />&nbsp;Cancel
              </button>
            </div>
          </div>
          <h4>{{ project.name }}</h4>
        </div>
      </div>
      <div class="ui-sub-container" v-if="localState.projAdd">
        <div class="ui-form-textinput">
          <div class="ui-label">New Project Name</div>
          <input v-model="localState.projNameTemp" />
        </div>
        <div class="ui-form-textinput">
          <div class="ui-label">New Project Description</div>
          <textarea class="textarea" v-model="localState.projDescriptionTemp"></textarea>
        </div>
        <div class="right">
          <button class="ui-button ui-button-green" @click="addNow()">
            Add
          </button>
          <button class="ui-button ui-button-red" @click="cancelAdd()">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
